import type { ISegmentExport } from '@feathr/blackbox';
import type { TProgressMode } from '@feathr/extender/components/ProgressCell';
import { toInProgressMode } from '@feathr/extender/components/ProgressCell';

interface IProgressInputs {
  state: ISegmentExport['state'];
  /** Undefined when the API omits the field, e.g. on an export that never started. */
  progress: ISegmentExport['progress'] | undefined;
}

/**
 * Derives the visual mode for a row's Progress column from the BE `state` and
 * `progress` fields.
 *
 * Ordering matters: terminal-but-not-ready (`stopped`/`failed`) wins over the
 * percent, and `ready`/`expired` always show 100% even if `progress` lags behind.
 *
 * @param inputs - The relevant subset of `ISegmentExport` fields.
 * @returns The progress display mode.
 *
 * @example
 *   getProgressMode({ state: 'exporting', progress: 31 })
 *   // → { kind: 'inProgress', percent: 31 }
 */
export function getProgressMode({ state, progress }: IProgressInputs): TProgressMode {
  if (state === 'stopped' || state === 'failed') {
    return { kind: 'notApplicable' };
  }

  if (state === 'ready' || state === 'expired') {
    return { kind: 'complete' };
  }

  return toInProgressMode(progress);
}
